"use client";

import React, { useEffect, useRef, useState, forwardRef, useId, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { XIcon } from "lucide-react";
import { cn } from "visuais/fmt";
import { useOutsideClick } from "hooks";

interface CustomDialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  triggerRef?: React.RefObject<HTMLElement | null>;
  layoutId?: string; // Para o layoutId do Framer Motion
}

/**
 * Custom Dialog com animações épicas
 *
 * Características:
 * - Expande do centro do trigger
 * - Animação de bounce/spring
 * - Overlay com blur
 * - Controle total das animações
 */
export const CustomDialog: React.FC<CustomDialogProps> = ({
  isOpen,
  onClose,
  children,
  className,
  triggerRef,
  layoutId = "custom-dialog",
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const id = useId();

  // Cria um layoutId único para esta instância
  const uniqueLayoutId = `${layoutId}-${id}`;

  // Função para calcular a transformação do trigger
  const getTriggerTransform = useCallback(() => {
    if (!triggerRef?.current) {
      return {
        x: 0,
        y: 0,
        width: 200,
        height: 50,
      };
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const windowCenterX = window.innerWidth / 2;
    const windowCenterY = window.innerHeight / 2;

    const offsetX = centerX - windowCenterX;
    const offsetY = centerY - windowCenterY;

    return {
      x: offsetX,
      y: offsetY,
      width: rect.width,
      height: rect.height,
    };
  }, [triggerRef]);

  // Estado para capturar a transformação do trigger quando o dialog abre
  const [triggerTransform, setTriggerTransform] = useState(() =>
    getTriggerTransform()
  );

  // Atualiza a transformação quando o dialog abre
  useEffect(() => {
    if (isOpen) {
      const transform = getTriggerTransform();
      setTriggerTransform(transform);

      // Esconde o trigger button durante a animação
      if (triggerRef?.current) {
        triggerRef.current.style.opacity = "0";
        triggerRef.current.style.pointerEvents = "none";
      }
    } else {
      // Restaura o trigger button quando fecha
      if (triggerRef?.current) {
        triggerRef.current.style.opacity = "1";
        triggerRef.current.style.pointerEvents = "auto";
      }
    }
  }, [isOpen, triggerRef, getTriggerTransform]);

  // Previne scroll do body quando dialog está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Fecha o dialog quando clicar fora
  useOutsideClick(dialogRef as React.RefObject<HTMLElement>, () => {
    if (isOpen) {
      onClose();
    }
  });

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.15,
              ease: "easeOut",
            }}
            className="fixed inset-0 bg-black/20 backdrop-blur-xs z-[10]"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Dialog Content */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 grid place-items-center z-[20] rounded-lg">
            <motion.div
              ref={dialogRef}
              layoutId={uniqueLayoutId}
              initial={{
                width: triggerTransform.width,
                height: triggerTransform.height,
                x: triggerTransform.x,
                y: triggerTransform.y,
                borderRadius: "8px",
              }}
              animate={{
                width: "95vw",
                height: "95vh",
                x: 0,
                y: 0,
                borderRadius: "8px",
              }}
              exit={{
                width: triggerTransform.width,
                height: triggerTransform.height,
                x: triggerTransform.x,
                y: triggerTransform.y,
                borderRadius: "8px",
              }}
              transition={{
                duration: 0.25,
                ease: [0.25, 0.46, 0.45, 0.94],
                type: "tween",
              }}
              style={{
                willChange: "transform, width, height",
                transform: "translateZ(0)",
              }}
              className={cn(
                "bg-popover border shadow-2xl flex flex-col rounded-lg",
                className
              )}
            >
              {/* Content */}
              <motion.div
                className="flex-1 w-full min-h-0 flex flex-col"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.1, duration: 0.15 }}
                style={{
                  height: "100%",
                  maxHeight: "100%",
                }}
              >
                {children}
              </motion.div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

/**
 * Custom Dialog Header
 */
export const CustomDialogHeader: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return (
    <motion.div
      className={cn(
        "flex gap-2 p-6 border-b rounded-t-lg shadow",
        className
      )}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: 0.05,
        duration: 0.15,
        ease: "easeOut",
      }}
    >
      {children}
    </motion.div>
  );
};

/**
 * Custom Dialog Title
 */
export const CustomDialogTitle: React.FC<{
  children: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
  onClose?: () => void;
}> = ({ children, className, showCloseButton = true, onClose }) => {
  return (
    <motion.div
      className={cn("flex items-center gap-3", className)}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        delay: 0.1,
        duration: 0.1,
        ease: "easeOut",
      }}
    >
      {/* Close Button */}
      {showCloseButton && onClose && (
        <motion.button
          className="p-1.5 rounded-md hover:bg-muted transition-colors flex-shrink-0"
          onClick={onClose}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            delay: 0.15,
            duration: 0.1,
            ease: "easeOut",
          }}
        >
          <XIcon className="w-4 h-4" />
          <span className="sr-only">Fechar</span>
        </motion.button>
      )}

      {/* Title */}
      <h2 className="text-lg font-semibold leading-none flex-1">{children}</h2>
    </motion.div>
  );
};

/**
 * Custom Dialog Description
 */
export const CustomDialogDescription: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return (
    <motion.p
      className={cn("text-sm text-muted-foreground", className)}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: 0.15,
        duration: 0.1,
        ease: "easeOut",
      }}
    >
      {children}
    </motion.p>
  );
};

/**
 * Custom Dialog Content
 */
export const CustomDialogContent: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return (
    <motion.div
      className={cn("flex-1 overflow-auto p-6 rounded-lg", className)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        delay: 0.2,
        duration: 0.15,
        ease: "easeOut",
      }}
      style={{
        maxHeight: "100%",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {children}
    </motion.div>
  );
};

/**
 * Custom Dialog Trigger
 *
 * Componente que serve como trigger para o dialog
 * Deve ser usado com o mesmo layoutId do CustomDialog
 */
export const CustomDialogTrigger = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    layoutId?: string;
    asChild?: boolean;
  }
>(
  (
    {
      className,
      layoutId = "custom-dialog",
      asChild = false,
      children,
      ...props
    },
    ref
  ) => {
    if (asChild && React.isValidElement(children)) {
      return (
        <motion.div layoutId={layoutId}>
          {React.cloneElement(children, {
            ref,
            ...props,
          } as any)}
        </motion.div>
      );
    }

    return (
      <motion.div layoutId={layoutId}>
        <button
          ref={ref}
          className={cn(
            "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
            className
          )}
          {...props}
        >
          {children}
        </button>
      </motion.div>
    );
  }
);

CustomDialogTrigger.displayName = "CustomDialogTrigger";
